import { BlogCategory, BlogPostInfo, PhotoInfo, ProjectInfo } from './types';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';
const TOKEN_KEY = 'idare_token';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, options: RequestInit = {}, auth = false): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  if (auth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (auth && res.status === 401) {
    clearToken();
    window.location.assign('/idare/login');
  }

  const json: ApiResponse<T> = await res.json();

  if (!res.ok || !json.success) {
    throw new Error(typeof json.message === 'string' ? json.message : 'İstek başarısız oldu');
  }

  return json.data as T;
}

// ---- Public ----
export const getProjects = () => request<ProjectInfo[]>('/projects');
export const getProject = (id: number) => request<ProjectInfo>(`/projects/${id}`);
export const getBlogPosts = () => request<BlogPostInfo[]>('/blog-posts');
export const getBlogPost = (slug: string) => request<BlogPostInfo>(`/blog-posts/${slug}`);
export const getBlogCategories = () => request<BlogCategory[]>('/blog-categories');
export const getPhotos = () => request<PhotoInfo[]>('/photos');

export const login = (username: string, password: string) =>
  request<{ token: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });

// ---- İdare (auth gerekli) ----
export const listAllProjects = () => request<ProjectInfo[]>('/idare/projects/', {}, true);
export const createProject = (data: Partial<ProjectInfo>) =>
  request<ProjectInfo>('/idare/projects/', { method: 'POST', body: JSON.stringify(data) }, true);
export const updateProject = (id: number, data: Partial<ProjectInfo>) =>
  request<ProjectInfo>(`/idare/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }, true);
export const deleteProject = (id: number) =>
  request<void>(`/idare/projects/${id}`, { method: 'DELETE' }, true);

export const listAllBlogPosts = () => request<BlogPostInfo[]>('/idare/blog-posts/', {}, true);
export const getBlogPostForAdmin = (id: number) =>
  request<BlogPostInfo>(`/idare/blog-posts/${id}`, {}, true);
export const createBlogPost = (data: Partial<BlogPostInfo> & { published: boolean }) =>
  request<BlogPostInfo>('/idare/blog-posts/', { method: 'POST', body: JSON.stringify(data) }, true);
export const updateBlogPost = (id: number, data: Partial<BlogPostInfo> & { published: boolean }) =>
  request<BlogPostInfo>(`/idare/blog-posts/${id}`, { method: 'PUT', body: JSON.stringify(data) }, true);
export const deleteBlogPost = (id: number) =>
  request<void>(`/idare/blog-posts/${id}`, { method: 'DELETE' }, true);

export const createBlogCategory = (data: { slug: string; name_en: string; name_tr: string }) =>
  request<BlogCategory>('/idare/blog-categories', { method: 'POST', body: JSON.stringify(data) }, true);

export const listAllPhotos = () => request<PhotoInfo[]>('/idare/photos/', {}, true);
export const createPhoto = (data: Partial<PhotoInfo>) =>
  request<PhotoInfo>('/idare/photos/', { method: 'POST', body: JSON.stringify(data) }, true);
export const updatePhoto = (id: number, data: Partial<PhotoInfo>) =>
  request<PhotoInfo>(`/idare/photos/${id}`, { method: 'PUT', body: JSON.stringify(data) }, true);
export const deletePhoto = (id: number) =>
  request<void>(`/idare/photos/${id}`, { method: 'DELETE' }, true);

export async function uploadImage(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/idare/uploads`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  });
  const json: ApiResponse<{ url: string }> = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(typeof json.message === 'string' ? json.message : 'Yükleme başarısız oldu');
  }
  return json.data as { url: string };
}
